'use client';

import { Box, Text, IconButton, Flex } from '@radix-ui/themes';
import { UseFormReturn } from 'react-hook-form';
import {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { GMapsPinDropper } from '@trashlab/sitesapp/src/organizations/piedmont/components/shared/address-picker/pin-dropper';
import { useGoogleMapsLoader } from '@trashlab/sitesapp/src/organizations/piedmont/hooks/use-google-maps-loader';
import { IconX } from '@tabler/icons-react';
import { FormInput } from '@trashlab/sitesapp/src/organizations/piedmont/template/components/shared/form-input';

interface AddressPickerProps {
  prefix: string;
  form: UseFormReturn<any>;
  onRotationChange?: (rotation: number) => void;
  hideMap?: boolean;
  customInitLabel?: string;
  id?: string;
  onAddressSelected?: (placeId: string) => void;
  rightIcon?: React.ReactNode;
  errorMessage?: string;
  addressStatus?: 'idle' | 'checking' | 'available' | 'unavailable';
}

export const AddressPicker = ({
  prefix,
  form,
  onRotationChange,
  hideMap = false,
  customInitLabel = 'Address',
  id,
  onAddressSelected,
  rightIcon,
  errorMessage,
  addressStatus,
}: AddressPickerProps) => {
  const [showFullForm, setShowFullForm] = useState(false);
  const [addressQuery, setAddressQuery] = useState('');
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const { isLoaded: isGoogleMapsLoaded } = useGoogleMapsLoader();
  const inputRef = useRef<HTMLInputElement>(null);

  const formValues = useMemo(
    () => ({
      streetAddress: form.watch(`${prefix}.streetAddressPartOne`) || '',
      city: form.watch(`${prefix}.city`) || '',
      state: form.watch(`${prefix}.state`) || '',
      zip: form.watch(`${prefix}.postalCode`) || '',
      lat: form.watch(`${prefix}.latitude`) || 0,
      lng: form.watch(`${prefix}.longitude`) || 0,
    }),
    [
      form.watch([
        `${prefix}.streetAddressPartOne`,
        `${prefix}.city`,
        `${prefix}.state`,
        `${prefix}.postalCode`,
        `${prefix}.latitude`,
        `${prefix}.longitude`,
      ]),
    ],
  );

  useEffect(() => {
    if (isGoogleMapsLoaded && window.google && google.maps) {
      if (!autocompleteService.current) {
        autocompleteService.current =
          new google.maps.places.AutocompleteService();
      }

      const dummyDiv = document.createElement('div');
      placesService.current = new google.maps.places.PlacesService(dummyDiv);
    }
  }, [isGoogleMapsLoaded]);

  useEffect(() => {
    const hasData = Object.values(formValues).some((value) =>
      typeof value === 'string' ? value.length > 0 : value !== 0,
    );

    if (hasData && (addressStatus === undefined || addressStatus === 'available')) {
      setShowFullForm(true);
    }
  }, [formValues, addressStatus]);

  const fetchPredictions = useCallback(
    async (query: string) => {
      if (!query || !isGoogleMapsLoaded || !autocompleteService.current) {
        setPredictions([]);
        return;
      }

      autocompleteService.current.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country: ['us', 'ca'] },
          // Empty types array allows all place types for more precise suggestions
          // This matches shop-v3's autocomplete behavior
          types: [],
        },
        (results, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && results) {
            setPredictions(results);
            setShowPredictions(true);
          } else {
            setPredictions([]);
            setShowPredictions(false);
          }
        },
      );
    },
    [isGoogleMapsLoaded],
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchPredictions(addressQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [addressQuery, fetchPredictions]);

  const handleSelectAddress = useCallback(
    (placeId: string) => {
      if (!placesService.current) return;

      placesService.current.getDetails(
        { placeId, fields: ['address_component', 'geometry'] },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            const streetNumber =
              place.address_components?.find((comp) =>
                comp.types.includes('street_number'),
              )?.long_name || '';

            const route =
              place.address_components?.find((comp) =>
                comp.types.includes('route'),
              )?.long_name || '';

            const city =
              place.address_components?.find((comp) =>
                comp.types.includes('locality'),
              )?.long_name || '';

            const state =
              place.address_components?.find((comp) =>
                comp.types.includes('administrative_area_level_1'),
              )?.short_name || '';

            const postalCode =
              place.address_components?.find((comp) =>
                comp.types.includes('postal_code'),
              )?.long_name || '';

            const county =
              place.address_components?.find((comp) =>
                comp.types.includes('administrative_area_level_2'),
              )?.long_name || '';

            form.setValue(
              `${prefix}.streetAddressPartOne`,
              `${streetNumber} ${route}`,
            );
            form.setValue(`${prefix}.city`, city);
            form.setValue(`${prefix}.state`, state);
            form.setValue(`${prefix}.postalCode`, postalCode);
            form.setValue(`${prefix}.county`, county);

            if (place.geometry?.location) {
              form.setValue(
                `${prefix}.latitude`,
                place.geometry.location.lat(),
              );
              form.setValue(
                `${prefix}.longitude`,
                place.geometry.location.lng(),
              );
            }

            setPredictions([]);
            setShowPredictions(false);
            setAddressQuery('');

            if (addressStatus === undefined || addressStatus === 'available') {
              setShowFullForm(true);
            }
            
            if (onAddressSelected) {
              setTimeout(() => {
                onAddressSelected(placeId);
              }, 0);
            }
          }
        },
      );
    },
    [form, prefix, addressStatus, onAddressSelected],
  );

  const handleReset = useCallback(() => {
    form.setValue(`${prefix}.streetAddressPartOne`, '');
    form.setValue(`${prefix}.streetAddressPartTwo`, '');
    form.setValue(`${prefix}.city`, '');
    form.setValue(`${prefix}.state`, '');
    form.setValue(`${prefix}.postalCode`, '');
    form.setValue(`${prefix}.latitude`, 0);
    form.setValue(`${prefix}.longitude`, 0);
    form.setValue(`${prefix}.county`, '');
    form.setValue(`${prefix}.containerRotation`, 0);

    setAddressQuery('');
    setShowFullForm(false);
  }, [form, prefix]);

  const handleAddressQueryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAddressQuery(e.target.value);
    },
    [],
  );

  const handlePredictionClick = useCallback(
    (placeId: string) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleSelectAddress(placeId);
    },
    [handleSelectAddress],
  );

  const error =
    errorMessage ||
    form.formState.errors[
      `${prefix}.streetAddressPartOne`
    ]?.message?.toString();

  return (
    <Box>
      <Box className="space-y-2" id={id}>
        {!showFullForm ? (
          <Box className="relative">
            <Flex direction="column" gap="2">
              <Text as="label" htmlFor="addressQuery">
                {customInitLabel}
              </Text>
              <Box className="relative">
                <input
                  ref={inputRef}
                  className={`w-full px-3 py-2 border rounded-md border-gray-300 ${
                    rightIcon ? 'pr-10' : ''
                  } ${error ? 'border-[var(--danger-color)]' : ''}`}
                  placeholder="Start typing your address, e.g. 123 Main..."
                  value={addressQuery}
                  onChange={handleAddressQueryChange}
                  onFocus={() => setShowPredictions(true)}
                  onBlur={() =>
                    setTimeout(() => setShowPredictions(false), 200)
                  }
                  id={`${prefix}-search`}
                  name={`${prefix}-search`}
                  data-field-name={`${prefix}Search`}
                  data-field-type="addressSearch"
                />
                {formValues.streetAddress && (
                  <Box className="absolute right-12 top-1/2 transform -translate-y-1/2">
                    <IconButton
                      variant="ghost"
                      className="text-[var(--primary-color)]"
                      onClick={handleReset}
                    >
                      <IconX />
                    </IconButton>
                  </Box>
                )}
                {rightIcon && (
                  <Box className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {rightIcon}
                  </Box>
                )}
                {/* Predictions dropdown - positioned directly below the input */}
                {showPredictions &&
                  predictions.length > 0 && (
                    <div
                      className="absolute left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
                      style={{
                        boxShadow:
                          '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        zIndex: 9999,
                        top: '100%',
                        marginTop: '4px',
                        backgroundColor: '#ffffff',
                        color: '#000000',
                      }}
                    >
                      {predictions.map((prediction) => (
                        <Box
                          key={prediction.place_id}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-gray-900 border-b border-gray-100 last:border-b-0"
                          onMouseDown={(e) => {
                            // Use onMouseDown instead of onClick to fire before blur
                            e.preventDefault();
                            handleSelectAddress(prediction.place_id);
                          }}
                        >
                          <Text size="2" style={{ color: 'inherit' }}>
                            {prediction.description}
                          </Text>
                        </Box>
                      ))}
                    </div>
                  )}
              </Box>
            </Flex>
            {error && (
              <Text size="2" className="text-[var(--danger-color)]">
                {error}
              </Text>
            )}
          </Box>
        ) : (
          <>
            <FormInput
              {...form.register(`${prefix}.streetAddressPartOne`)}
              name={`${prefix}.streetAddressPartOne`}
              label={customInitLabel}
              placeholder="Street address"
              required
              containerClassName="w-full"
              rightElement={
                <Flex gap="2">
                  {formValues.streetAddress && (
                    <IconButton
                      variant="ghost"
                      className="text-[var(--primary-color)]"
                      onClick={handleReset}
                    >
                      <IconX />
                    </IconButton>
                  )}
                  {rightIcon && <Box>{rightIcon}</Box>}
                </Flex>
              }
            />
            <FormInput
              {...form.register(`${prefix}.streetAddressPartTwo`)}
              name={`${prefix}.streetAddressPartTwo`}
              label="Apartment, suite, etc."
              placeholder="Apt, Suite, etc. (optional)"
              containerClassName="w-full"
            />
            {/* Stack vertically on mobile, 3-column grid on sm+ */}
            <Box className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <FormInput
                {...form.register(`${prefix}.city`)}
                name={`${prefix}.city`}
                label="City"
                placeholder="City"
                required
                containerClassName="w-full"
              />
              <FormInput
                {...form.register(`${prefix}.state`)}
                name={`${prefix}.state`}
                label="State"
                placeholder="State"
                required
                containerClassName="w-full"
              />
              <FormInput
                {...form.register(`${prefix}.postalCode`)}
                name={`${prefix}.postalCode`}
                label="ZIP code"
                placeholder="ZIP code"
                required
                containerClassName="w-full"
              />
            </Box>
          </>
        )}
      </Box>

      {showFullForm && !hideMap && (
        <GMapsPinDropper
          placeId={`${formValues.streetAddress} ${formValues.city} ${formValues.state} ${formValues.zip}`}
          position={{ lat: formValues.lat, lng: formValues.lng }}
          onChange={(position) => {
            form.setValue(`${prefix}.latitude`, position.lat);
            form.setValue(`${prefix}.longitude`, position.lng);
          }}
          initialRotation={form.watch(`${prefix}.containerRotation`) || 0}
          onRotationChange={(rotation) => {
            form.setValue(`${prefix}.containerRotation`, rotation);
            onRotationChange?.(rotation);
          }}
          onReset={handleReset}
        />
      )}
    </Box>
  );
};